import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { ToggleGroup, ToggleGroupItem } from '@klareospace/pharos-lib-web-utils';

import { IMPORT_TYPE } from '../config';
import type { ImportType, ImportTypeConfig } from '../config';

// ─── Constants ──────────────────────────────────────────────────────────────

const IMPORT_TYPE_LABEL_ID = 'import-type-label';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ImportTypeSelectorProps {
  onValueChange: (type: ImportType) => void;
  options: readonly ImportTypeConfig[];
  value: ImportType;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Import type selector shown atop the import screen, driven by `IMPORT_TYPE_CONFIGS`. */
export function ImportTypeSelector({ onValueChange, options, value }: ImportTypeSelectorProps): React.JSX.Element {
  const { t } = useTranslation('imports');

  function handleValueChange(nextValue: string): void {
    // Radix's ToggleGroup emits an empty string when the pressed item is deselected — clicking
    // the already-selected item must not clear the selection.
    if (!isImportType(nextValue)) return;
    onValueChange(nextValue);
  }

  return (
    <div aria-labelledby={IMPORT_TYPE_LABEL_ID} className="flex flex-wrap items-center gap-2" role="group">
      <span className="text-sm font-semibold text-foreground" id={IMPORT_TYPE_LABEL_ID}>
        {t('importType.label')}
      </span>
      <ToggleGroup onValueChange={handleValueChange} type="single" value={value} variant="outline">
        {options.map((option) => (
          <ToggleGroupItem disabled={!option.active} key={option.type} value={option.type}>
            {t(option.labelKey)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isImportType(value: string): value is ImportType {
  return (Object.values(IMPORT_TYPE) as string[]).includes(value);
}
